import { pluralize } from 'humanize-plus';

import useSharingSettingsTracking from '@/components/sharing-settings/useSharingSettingsTracking';

import { useFlashesStore, usePartnersStore } from '@/stores';
import { formatList } from '@/utils';

import type { SharingPartnerRow } from './types';

type RemovablePartnerType = Pick<SharingPartnerRow, 'uuid' | 'partner'>;

function partnerNames(partners: RemovablePartnerType[], uuids: string[]) {
  return formatList(
    partners
      .filter(({ uuid }) => uuids.includes(uuid))
      .map(({ partner }) => partner.name),
  );
}

/**
 * Deletes the selected partnerships and reports the outcome in a flash. The
 * partners that could not be removed stay in the table, and so stay selected
 * for a retry.
 *
 * @param partners - The partners selected when the modal opened
 * @returns Whether any partner was removed
 */
export async function removeBulkPartners(
  partners: RemovablePartnerType[],
): Promise<boolean> {
  const { addSuccessFlash, addWarningFlash, addErrorFlash } = useFlashesStore();

  const result = await usePartnersStore().bulkDeletePartnerships(
    partners.map(({ uuid }) => uuid),
  );

  useSharingSettingsTracking().trackBulkRemoveConfirmed(
    partners.length,
    result?.removed.length ?? 0,
  );

  if (!result?.removed.length) {
    addErrorFlash({
      message: 'We were not able to remove these partners',
      description: 'No partners were removed, please try again',
    });
    return false;
  }

  const { removed, failed } = result;
  if (failed.length) {
    addWarningFlash({
      message: `${removed.length} of ${partners.length} partners removed`,
      description: `Could not remove: ${partnerNames(partners, failed)}`,
    });
    return true;
  }

  addSuccessFlash({
    message: `${removed.length} ${pluralize(removed.length, 'partner')} removed`,
    description: 'It will take a few minutes to fully delete their data',
  });
  return true;
}
